/*
 * PDP image preload — download product gallery slides 2..N in the background
 * so clicking a thumbnail or arrow shows the next image instantly (from
 * cache) rather than triggering a lazysizes fetch with a visible fade-in.
 *
 * Gated conservatively so mobile / slow / metered users aren't billed for
 * bytes they may never look at:
 *   - viewport ≥ 768px (Aromantic is ~98% desktop; mobile keeps stock lazysizes)
 *   - fast network (Chrome/Edge report effectiveType === '4g', no Data Saver)
 *   - adequate device memory (skip <2 GB — correlates with cheap phones)
 *   - Safari/Firefox return `navigator.connection === undefined` → conservative
 *     skip; those users keep the existing lazysizes behaviour.
 *
 * Two-tier timing based on confirmed connection quality:
 *   - Standard fast (effectiveType='4g'): wait for LCP paint before preloading
 *     so we don't compete with the first-image download.
 *   - Confirmed fast (downlink ≥ 5 Mbps AND rtt < 100 ms): fire immediately
 *     when the deferred script boots — seconds earlier than LCP on slower
 *     connections. Fetchpriority=low keeps the LCP image ahead in the
 *     bandwidth queue so we still can't hurt LCP.
 */
(function () {
  if (window.innerWidth < 768) return;

  var conn = navigator.connection;
  if (!conn) return; // Safari/Firefox — conservative skip
  if (conn.effectiveType !== '4g') return; // skip 3G / 2G / slow-2g
  if (conn.saveData) return; // respect explicit Data-Saver
  if (typeof navigator.deviceMemory === 'number' && navigator.deviceMemory < 2) return;

  // Confirmed fast = fibre / cable / 5G territory. Chrome caps downlink at
  // 10 Mbps for privacy, so 5+ means comfortably above the '4g' threshold.
  // rtt < 100 rules out satellite / congested 4G cells / airplane wifi.
  // Missing rtt (=0 or undefined) → treat as unknown, fall through to standard.
  var isConfirmedFast = conn.downlink >= 5 && conn.rtt > 0 && conn.rtt < 100;

  function preloadRemainingSlides() {
    var slider = document.querySelector('custom-product-slider');
    if (!slider) return;

    // Non-first slides only — the first slide is CWV-006 eager-loaded already.
    var others = slider.querySelectorAll(
      '.product-images__slide:not(:first-child) img.lazyload[data-srcset]'
    );
    if (!others.length) return;

    // Match the effective sizes that lazysizes will request on the <img>.
    // lazysizes with data-sizes="auto" computes size from the ACTUAL rendered
    // element width. Measure the slider so the preload picks the SAME srcset
    // variant lazysizes will pick on click — otherwise browser downloads a
    // near-duplicate variant for nothing (~30% wasted bytes at desktop
    // widths). Fall back to 50vw if measurement is unavailable.
    var actualWidth = slider.offsetWidth;
    var sizesForPreload = actualWidth > 0 ? actualWidth + 'px' : '50vw';

    others.forEach(function (img) {
      var link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.imageSrcset = img.dataset.srcset;
      link.imageSizes = sizesForPreload;
      link.setAttribute('fetchpriority', 'low');
      document.head.appendChild(link);
    });
  }

  if (isConfirmedFast) {
    // Confirmed-fast tier: fire immediately, don't wait for LCP.
    // The slider needs to be in the DOM though — this script is deferred so
    // that's true by the time we run.
    preloadRemainingSlides();
    return;
  }

  // Standard-fast tier: wait for LCP paint before starting background work
  // so we don't compete with the first image's download.
  try {
    var po = new PerformanceObserver(function () {
      po.disconnect();
      preloadRemainingSlides();
    });
    po.observe({ type: 'largest-contentful-paint', buffered: true });
  } catch (e) {
    // Safety net for very old browsers — should be unreachable given the
    // Chrome/Edge gate above.
    if (document.readyState === 'complete') {
      preloadRemainingSlides();
    } else {
      window.addEventListener('load', preloadRemainingSlides, { once: true });
    }
  }
})();
